import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Radio, Tv, UserCheck, X, Search } from "lucide-react";

const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2 text-sm text-white focus:outline-none focus:border-red-400";
const lbl = "block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5";

export default function LiveBroadcastPanel() {
  const [live, setLive] = useState(null);
  const [channel, setChannel] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [keepers, setKeepers] = useState([]);
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);

  const [dirty, setDirty] = useState(false);

  const load = () => {
    api.get("/admin/live").then(({ data }) => {
      setLive(data);
      setDirty((d) => { if (!d) { setChannel(data.twitch_channel || ""); setTitle(data.title || ""); } return d; });
    }).catch(() => {});
    api.get("/admin/scorekeepers").then(({ data }) => setKeepers(data || [])).catch(() => {});
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get("/members/search", { params: { q: q.trim() } }).then(({ data }) => setRes((data || []).slice(0, 6))).catch(() => setRes([])), 300);
    return () => clearTimeout(t);
  }, [q]);

  const save = async (patch, msg) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/live", patch);
      setLive((l) => ({ ...l, ...data }));
      if ("twitch_channel" in patch) { setChannel(data.twitch_channel || ""); setTitle(data.title || ""); setDirty(false); }
      toast.success(msg);
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); load(); }
  };
  const saveChannel = () => save({ twitch_channel: channel, title }, channel.trim() ? `Streaming from twitch.tv/${channel.trim()}` : "Stream turned off");
  const setRole = async (u, role) => {
    try { await api.put(`/admin/members/${u.id}/role`, { role }); toast.success(role === "scorekeeper" ? `${u.name} is now a Scorekeeper` : `${u.name} back to player`); setQ(""); setRes([]); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!live) return null;
  const mc = live.main_court;
  const cands = live.candidates || [];
  const current = cands.find((c) => c.main_court);

  return (
    <div className="space-y-5" data-testid="live-broadcast-panel">
      <form className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end" onSubmit={(e) => { e.preventDefault(); saveChannel(); }}>
        <div>
          <label className={lbl}>Twitch channel</label>
          <input data-testid="live-twitch-channel" value={channel} onChange={(e) => { setChannel(e.target.value); setDirty(true); }} placeholder="thepicklebullpen" className={input} />
          {live.twitch_channel ? <div className="text-[10px] text-zinc-500 mt-1" data-testid="live-channel-saved">Saved: twitch.tv/<b className="text-white">{live.twitch_channel}</b></div>
            : <div className="text-[10px] text-zinc-600 mt-1" data-testid="live-channel-none">No channel saved yet — /live shows "Stream offline".</div>}
        </div>
        <div>
          <label className={lbl}>Broadcast title <span className="text-zinc-600 normal-case tracking-normal">(optional)</span></label>
          <input data-testid="live-title" value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} placeholder="Championship Sunday · Main Court" className={input} maxLength={80} />
        </div>
        <button type="submit" data-testid="live-save-channel" disabled={busy}
          className="h-[38px] px-4 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-red-500 disabled:opacity-40 flex items-center gap-1.5 self-start md:self-auto"><Tv className="w-4 h-4" /> Save</button>
      </form>

      <div className="border border-red-500/30 bg-red-500/[0.04] p-3" data-testid="live-main-court">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-red-400"><Radio className="w-4 h-4 animate-pulse" /> Main Court — scoreboard on /live</div>
          {mc && <button data-testid="live-clear-main" onClick={() => save({ clear_main_court: true }, "Main Court cleared")} className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-1"><X className="w-3 h-3" /> Clear</button>}
        </div>
        {current ? (
          <div className="text-sm mb-2" data-testid="live-main-current"><span className="text-zinc-500">Now:</span> <b>{current.side_a}</b> vs <b>{current.side_b}</b> <span className="text-zinc-500">· {current.tournament}{current.court ? ` · Court ${current.court}` : ""}</span></div>
        ) : mc ? <div className="text-xs text-zinc-500 mb-2" data-testid="live-main-stale">Main Court match {mc.key} is no longer active — pick a new one.</div>
          : <div className="text-xs text-zinc-500 mb-2">No Main Court set. Choose an active match below.</div>}
        {cands.length === 0 ? <div className="text-xs text-zinc-600" data-testid="live-no-candidates">No active matches — start a tournament and assign courts first.</div> : (
          <select data-testid="live-main-select" value={current ? `${current.tid}|${current.key}` : ""} onChange={(e) => { const [tid, key] = e.target.value.split("|"); if (tid) save({ main_court: { tid, key } }, "Main Court updated — /live scoreboard is on it"); }} className={input}>
            <option value="">Pick the Main Court match…</option>
            {cands.map((c) => <option key={`${c.tid}|${c.key}`} value={`${c.tid}|${c.key}`}>{c.court ? `Ct ${c.court} · ` : ""}{c.side_a} vs {c.side_b} — {c.tournament} · {c.label}</option>)}
          </select>
        )}
      </div>

      <div className="border border-pb-border p-3" data-testid="scorekeepers-panel">
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-pb-cyan mb-2"><UserCheck className="w-4 h-4" /> Scorekeepers <span className="text-zinc-600 normal-case tracking-normal font-normal">— only see the Score Entry page, nothing else in Admin</span></div>
        <div className="relative mb-2">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-zinc-500" />
          <input data-testid="sk-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members to make a Scorekeeper…" className={`${input} pl-8`} />
          {res.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 border border-pb-border bg-pb-surface shadow-xl" data-testid="sk-search-results">
              {res.map((u) => (
                <button key={u.id} data-testid={`sk-make-${u.id}`} onClick={() => setRole(u, "scorekeeper")} className="w-full text-left px-3 py-2 text-xs hover:bg-pb-elevated flex justify-between">
                  <span>{u.name}</span><span className="text-zinc-500">{u.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {keepers.length === 0 ? <div className="text-xs text-zinc-600" data-testid="sk-none">No scorekeepers yet.</div> : (
          <div className="flex flex-wrap gap-2" data-testid="sk-list">
            {keepers.map((k) => (
              <div key={k.id} className="flex items-center gap-2 border border-pb-border bg-pb-elevated px-2.5 py-1.5 text-xs" data-testid={`sk-keeper-${k.id}`}>
                <span className="font-semibold">{k.name}</span><span className="text-zinc-500">{k.email}</span>
                <button data-testid={`sk-remove-${k.id}`} onClick={() => setRole(k, "player")} className="text-zinc-500 hover:text-red-400" title="Remove scorekeeper role"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
